import React, { useState, useEffect, useRef } from "react";
import { quizStages as solarQuizData } from "./quizData";
import Swal from "sweetalert2";
import "./styles.css";

import correctSoundFile from "../../assets/sounds/correct.mp3";
import wrongSoundFile from "../../assets/sounds/wrong.mp3";

const SolarQuiz = () => {
  const [started, setStarted] = useState(false);
  const [level, setLevel] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [isOptionDisabled, setIsOptionDisabled] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [adPaused, setAdPaused] = useState(false);

  // debug message last received (useful for console/UI debugging)
  const lastMsgRef = useRef(null);

  const correctSound = useRef(new Audio(correctSoundFile));
  const wrongSound = useRef(new Audio(wrongSoundFile));

  const currentLevel = solarQuizData.find((lvl) => lvl.level === level);
  const currentQuestion = currentLevel.questions[currentQuestionIndex];

  /*******************************
   * Robust message handling
   *
   * We'll accept many message shapes:
   * - plain string: "pauseQuiz" / "resumeQuiz"
   * - JSON string: '{"type":"pauseQuiz"}'
   * - object: { type: 'pauseQuiz' } or { action: 'pauseQuiz' }
   * - event.detail, event.nativeEvent.data (some WebView variants)
   *******************************/
  useEffect(() => {
    const interpretMessage = (raw) => {
      if (!raw) return null;
      // if it's already an object
      if (typeof raw === "object") {
        // common fields
        if (raw.type) return raw.type;
        if (raw.action) return raw.action;
        if (raw.message) return raw.message;
        // React Native WebView sometimes wraps: {nativeEvent: {data: ...}}
        if (raw.nativeEvent && raw.nativeEvent.data) {
          try {
            const parsed = JSON.parse(raw.nativeEvent.data);
            if (parsed && parsed.type) return parsed.type;
          } catch (e) {
            return raw.nativeEvent.data;
          }
        }
        return null;
      }

      // if string
      if (typeof raw === "string") {
        // try parse JSON
        try {
          const parsed = JSON.parse(raw);
          if (parsed && (parsed.type || parsed.action || parsed.message)) {
            return parsed.type || parsed.action || parsed.message;
          }
        } catch (e) {
          // not JSON, fallthrough
        }
        return raw;
      }

      return null;
    };

    const handleMessage = (event) => {
      // many webviews put data in different places
      const payloadCandidates = [
        event && event.data,
        event && event.detail,
        event && event.message,
        event && event.nativeEvent && event.nativeEvent.data,
        event && event.srcElement && event.srcElement.data,
      ];

      // find the first non-null candidate
      let raw = null;
      for (const c of payloadCandidates) {
        if (c !== undefined && c !== null) {
          raw = c;
          break;
        }
      }

      // sometimes message is passed as event (string)
      if (!raw && typeof event === "string") raw = event;

      const interpreted = interpretMessage(raw);
      lastMsgRef.current = { raw, interpreted, timestamp: Date.now() };
      console.log("📩 Received raw message:", raw, "=> interpreted:", interpreted);

      if (!interpreted) return;

      // normalize to lowercase string
      const type = String(interpreted).trim();

      if (type === "pauseQuiz" || type === "pause" || type === "adOpened") {
        console.log("⏸ pause signal received");
        setIsPaused(true);
        setAdPaused(true);
      } else if (type === "resumeQuiz" || type === "resume" || type === "adClosed") {
        // we do NOT auto-resume; show resume button overlay instead
        console.log("▶ resume signal received (will show resume overlay)");
        setIsPaused(true);
        setAdPaused(true);
      } else {
        // debug - if your native sends another custom string, you will see it in console
        console.log("ℹ️ Unhandled message type:", type);
      }
    };

    // Listen to various events that might be used by WebView/native
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage); // Android legacy
    window.addEventListener("admob-message", handleMessage); // custom event possibility

    // Fallbacks: Page visibility and blur/focus
    const handleVisibility = () => {
      if (document.hidden) {
        console.log("document hidden -> pausing");
        setIsPaused(true);
        setAdPaused(true);
      } else {
        // don't auto-resume on visibilitychange
        console.log("document visible -> waiting for explicit resume");
      }
    };
    const handleBlur = () => {
      console.log("window blur -> pausing");
      setIsPaused(true);
      setAdPaused(true);
    };
    const handleFocus = () => {
      console.log("window focus -> waiting for explicit resume");
      // stay paused until resume clicked
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
      window.removeEventListener("admob-message", handleMessage);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  /*******************************
   * Load saved progress
   *******************************/
  useEffect(() => {
    try {
      const savedProgress = JSON.parse(localStorage.getItem("solarQuizProgress"));
      if (savedProgress) {
        Swal.fire({
          title: "Continue Quiz?",
          text: `You left off at Level ${savedProgress.level}. Continue or start over?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Continue",
          cancelButtonText: "Start Over",
          allowOutsideClick: false,
        }).then((result) => {
          if (result.isConfirmed) {
            setLevel(savedProgress.level);
            setScore(savedProgress.score);
            setCurrentQuestionIndex(savedProgress.currentQuestionIndex);
          } else {
            localStorage.removeItem("solarQuizProgress");
          }
          setStarted(true);
        });
      }
    } catch (e) {
      console.warn("Could not parse saved progress", e);
    }
  }, []);

  /*******************************
   * Auto-save progress whenever relevant values change
   *******************************/
  useEffect(() => {
    if (!started) return;
    saveProgress(level, score, currentQuestionIndex);
    // intentionally no debounce for correctness; browser localStorage is cheap here
  }, [level, score, currentQuestionIndex, started]);

  /*******************************
   * Timer countdown (stops on any pause including adPaused)
   *******************************/
  useEffect(() => {
    if (!started || isPaused || adPaused) return;

    if (timeLeft <= 0) {
      handleAnswer(null);
      return;
    }

    const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, started, isPaused, adPaused]);

  /*******************************
   * Answer handling (preserve previous behavior)
   *******************************/
  const handleAnswer = (option) => {
    if (!started) return;
    setIsOptionDisabled(true);
    setIsPaused(true);

    const isCorrect = option === currentQuestion.correct;
    if (isCorrect) {
      correctSound.current.pause();
      correctSound.current.currentTime = 0;
      correctSound.current.play();
      // use functional update to ensure correct value
      setScore((prev) => {
        const newScore = prev + 1;
        // save progress after score update (important)
        saveProgress(level, newScore, currentQuestionIndex + 1);
        return newScore;
      });
      Swal.fire({
        icon: "success",
        title: "✅ Correct!",
        timer: 1000,
        showConfirmButton: false,
      }).then(() => {
        setIsPaused(false);
        nextQuestion();
      });
    } else {
      wrongSound.current.pause();
      wrongSound.current.currentTime = 0;
      wrongSound.current.play();
      Swal.fire({
        icon: "error",
        title: "❌ Wrong!",
        timer: 1000,
        showConfirmButton: false,
      }).then(() => {
        setIsPaused(false);
        nextQuestion();
      });
    }
  };

  const nextQuestion = () => {
    setIsOptionDisabled(false);
    setTimeLeft(20);

    if (currentQuestionIndex + 1 < currentLevel.questions.length) {
      setCurrentQuestionIndex((prev) => {
        const nextIndex = prev + 1;
        saveProgress(level, score, nextIndex);
        return nextIndex;
      });
    } else {
      evaluateLevel();
    }
  };

  const evaluateLevel = () => {
    setIsPaused(true);
    const percentage = (score / currentLevel.questions.length) * 100;

    if (percentage >= 80) {
      Swal.fire({
        title: `🎉 Congratulations!`,
        text: `You passed Level ${level} (${currentLevel.rankTitle}) with ${percentage.toFixed(0)}%`,
        icon: "success",
        confirmButtonText: level < 10 ? "Next Level" : "Finish",
        allowOutsideClick: false,
      }).then(() => {
        if (level < 10) {
          const nextLevel = level + 1;
          setLevel(nextLevel);
          resetLevel();
          setIsPaused(false);
          saveProgress(nextLevel, 0, 0);
        } else {
          Swal.fire({
            title: "🏆 You are now a Solar Grandmaster!",
            text: "You’ve completed all levels successfully.",
            icon: "success",
          });
          localStorage.removeItem("solarQuizProgress");
          setStarted(false);
          setIsPaused(false);
        }
      });
    } else {
      Swal.fire({
        title: `😞 You scored ${percentage.toFixed(0)}%`,
        text: "You need at least 80% to pass. Try again!",
        icon: "error",
        confirmButtonText: "Retry Level",
        allowOutsideClick: false,
      }).then(() => {
        resetLevel();
        setIsPaused(false);
        saveProgress(level, 0, 0);
      });
    }
  };

  const resetLevel = () => {
    setScore(0);
    setCurrentQuestionIndex(0);
    setTimeLeft(20);
    setIsOptionDisabled(false);
  };

  const saveProgress = (levelParam, scoreParam, currentQuestionIndexParam) => {
    try {
      localStorage.setItem(
        "solarQuizProgress",
        JSON.stringify({
          level: levelParam,
          score: scoreParam,
          currentQuestionIndex: currentQuestionIndexParam,
        })
      );
    } catch (e) {
      console.warn("Could not save progress", e);
    }
  };

  const progressWidth = `${(timeLeft / 20) * 100}%`;

  const startQuiz = () => {
    setStarted(true);
    setIsPaused(false);
    setAdPaused(false);
    resetLevel();
  };

  /*******************************
   * Resume button handler (user must click)
   *******************************/
  const handleUserResume = () => {
    // clear ad pause flags and resume timer
    setAdPaused(false);
    setIsPaused(false);
    // ensure time doesn't jump — leave timeLeft as-is
    console.log("User clicked resume -> continuing quiz");
  };

  /*******************************
   * UI rendering
   *******************************/
  if (!started) {
    return (
      <div className="quiz-start-screen">
        <h1 className="quiz-title">🌞 Solar Quiz Challenge</h1>
        <p className="quiz-subtitle">
          Test your solar knowledge across <b>10 levels</b> — from Novice to Solar Grandmaster!
        </p>
        <button className="start-btn" onClick={startQuiz}>
          Start Quiz
        </button>
      </div>
    );
  }

  // Show ad pause overlay (user must click to resume)
  if (adPaused) {
    return (
      <div className="pause-overlay">
        <h2>⏸ Game Paused</h2>
        <p>An ad was detected or the app lost focus. Click below to resume the quiz when you're ready.</p>

        <div style={{ margin: "12px 0" }}>
          <button className="resume-btn" onClick={handleUserResume}>
            ▶ Resume Game
          </button>
        </div>

        <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
          <div>
            Tip: If this overlay appears without you seeing an ad, check your native code to ensure it sends
            <code> "pauseQuiz" </code>
            and
            <code> "resumeQuiz" </code>
            messages to the WebView.
          </div>
          <div style={{ marginTop: 6 }}>
            <strong>Last message (debug):</strong>{" "}
            <span style={{ fontFamily: "monospace", fontSize: 12 }}>
              {lastMsgRef.current ? JSON.stringify(lastMsgRef.current) : "none"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <h1 className="quiz-level">
        Level {level}/10: {currentLevel.title}
      </h1>
      <h2 className="quiz-rank">{currentLevel.rankTitle}</h2>

      <div className="progress-bar">
        <div
          className={`progress-fill ${timeLeft <= 3 ? "danger" : ""}`}
          style={{ width: progressWidth }}
        ></div>
      </div>

      <div className="question-box">
        <h3>
          Question {currentQuestionIndex + 1} of {currentLevel.questions.length}
        </h3>
        <p>{currentQuestion.question}</p>

        <div className="options-container">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(option)}
              disabled={isOptionDisabled}
              className={`option-btn ${isOptionDisabled ? "disabled" : ""}`}
            >
              {option}
            </button>
          ))}
        </div>

        <div className="quiz-status">
          ⏳ {timeLeft}s | ✅ {score}/{currentQuestionIndex + 1}
        </div>
      </div>
    </div>
  );
};

export default SolarQuiz;
